import React, { useEffect, useMemo, useState } from 'react';
import {
  getKnownChannels,
  generateIncomeDeck,
  generateChannelReport,
  KnownChannel,
} from '../../services/adminApi';
import { Modal, Button, Pill, Spinner } from '../ui';

interface IncomeDeckModalProps {
  onClose: () => void;
}

type OutputType = 'presentation' | 'document';
type PeriodPreset = 'single' | 'last3' | 'last6' | 'last12' | 'ytd' | 'all' | 'custom';

interface DeckResult {
  deckUrl?: string;
  exportUrl?: string;
  status?: string;
  truncationApplied?: string;
}

const IncomeDeckModal: React.FC<IncomeDeckModalProps> = ({ onClose }) => {
  const [channels, setChannels] = useState<KnownChannel[]>([]);
  const [loadingChannels, setLoadingChannels] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [outputType, setOutputType] = useState<OutputType>('presentation');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [periodPreset, setPeriodPreset] = useState<PeriodPreset>('last6');
  const [customFrom, setCustomFrom] = useState<string>('');
  const [customTo, setCustomTo] = useState<string>('');
  const [singleMonth, setSingleMonth] = useState<string>(currentMonthKey());

  const [generating, setGenerating] = useState(false);
  const [progressLog, setProgressLog] = useState<string[]>([]);
  const [result, setResult] = useState<DeckResult | null>(null);
  const [genError, setGenError] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await getKnownChannels();
        if (!mounted) return;
        setChannels(res.channels);
      } catch (err: any) {
        if (!mounted) return;
        setLoadError(err?.message || 'Failed to load channels');
      } finally {
        if (mounted) setLoadingChannels(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  const filteredChannels = useMemo(() => {
    if (!searchQuery) return channels;
    const q = searchQuery.toLowerCase();
    return channels.filter(c => c.channelName.toLowerCase().includes(q));
  }, [channels, searchQuery]);

  const allFilteredSelected = filteredChannels.length > 0 && filteredChannels.every(c => selectedIds.has(c.channelId));

  const { from, to } = useMemo(
    () => computePeriodBounds(periodPreset, customFrom, customTo, singleMonth),
    [periodPreset, customFrom, customTo, singleMonth],
  );

  const scopeLabel = useMemo(
    () => computeScopeLabel(periodPreset, from, to),
    [periodPreset, from, to],
  );

  const canSubmit = selectedIds.size > 0 && !!from && !!to && from <= to && !generating;
  const isReport = outputType === 'document';

  const handleSubmit = async () => {
    setGenError(null);
    setResult(null);
    setProgressLog([]);
    setGenerating(true);
    const generate = isReport
      ? (onEvent: (ev: any) => void) =>
          generateChannelReport({ channelIds: Array.from(selectedIds), from, to, scopeLabel }, onEvent)
      : (onEvent: (ev: any) => void) =>
          generateIncomeDeck({ channelIds: Array.from(selectedIds), from, to }, onEvent);
    try {
      await generate(
        (ev) => {
          if (ev.type === 'started') {
            setProgressLog(p => [...p, `Started. ${ev.channelIds.length} channel(s), ${ev.from} – ${ev.to}`]);
          } else if (ev.type === 'data_assembled') {
            setProgressLog(p => [...p, `Data: ${ev.channelsWithData} channels have data, $${ev.portfolio.totalUsd.toFixed(2)} total`]);
          } else if (ev.type === 'prompt_built') {
            const unit = isReport ? 'sections' : 'slides';
            setProgressLog(p => [...p, `Prompt built (${ev.promptLength} chars, ${ev.numCards} ${unit}${ev.truncationApplied && ev.truncationApplied !== 'none' ? `, truncation: ${ev.truncationApplied}` : ''})`]);
          } else if (ev.type === 'gamma_progress') {
            const msg = ev.progress?.message || `${ev.progress?.status} (${ev.progress?.elapsedSeconds ?? 0}s)`;
            setProgressLog(p => p.length && p[p.length - 1].startsWith('Gamma: ') ? [...p.slice(0, -1), `Gamma: ${msg}`] : [...p, `Gamma: ${msg}`]);
          } else if (ev.type === 'complete') {
            setProgressLog(p => [...p, 'Done.']);
            setResult({
              deckUrl: ev.deck?.gammaUrl || ev.deck?.url,
              exportUrl: ev.deck?.exportUrl,
              status: ev.deck?.status,
              truncationApplied: ev.truncationApplied,
            });
          } else if (ev.type === 'error') {
            setGenError(ev.error || 'Generation failed');
          }
        },
      );
    } catch (err: any) {
      setGenError(err?.message || 'Network error during deck generation');
    } finally {
      setGenerating(false);
    }
  };

  const toggleChannel = (channelId: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(channelId)) next.delete(channelId);
      else next.add(channelId);
      return next;
    });
  };

  const toggleSelectAllFiltered = () => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (allFilteredSelected) {
        for (const c of filteredChannels) next.delete(c.channelId);
      } else {
        for (const c of filteredChannels) next.add(c.channelId);
      }
      return next;
    });
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={isReport ? 'Generate Channel Report (PDF)' : 'Generate Income Deck'}
      width={720}
      ariaLabel="Generate channel deck or report"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={generating}>Cancel</Button>
          <Button variant="primary" onClick={handleSubmit} disabled={!canSubmit}>
            {generating ? 'Generating…' : isReport ? 'Generate Report' : 'Generate Deck'}
          </Button>
        </>
      }
    >
      {/* Output type */}
      <div style={styles.section}>
        <div style={styles.sectionTitle}>Output</div>
        <div style={styles.chipRow}>
          {([
            { v: 'presentation', label: 'Presentation deck' },
            { v: 'document', label: 'PDF report' },
          ] as Array<{ v: OutputType; label: string }>).map(o => (
            <Pill
              key={o.v}
              tone="neutral"
              active={outputType === o.v}
              disabled={generating}
              onClick={() => {
                if (o.v === outputType) return;
                setOutputType(o.v);
                // Clear any prior result so its labels can't mislabel the new output type.
                setResult(null);
                setGenError(null);
                setProgressLog([]);
              }}
            >
              {o.label}
            </Pill>
          ))}
        </div>
        <div style={styles.muted}>
          {isReport
            ? 'A client-ready PDF earnings report (Gamma document) with the Soft Blue theme.'
            : 'A slide presentation (Gamma) with the Soft Blue theme.'}
        </div>
      </div>

      {/* Channel picker */}
      <div style={styles.section}>
        <div style={styles.sectionTitle}>
          Channels <span style={styles.muted}>({selectedIds.size} selected)</span>
        </div>

        <div style={styles.searchRow}>
          <input
            type="text"
            placeholder="Search channels..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={styles.searchInput}
          />
          <Button size="sm" variant="ghost" onClick={toggleSelectAllFiltered} disabled={filteredChannels.length === 0}>
            {allFilteredSelected ? 'Deselect filtered' : 'Select all filtered'}
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setSelectedIds(new Set())} disabled={selectedIds.size === 0}>
            Clear
          </Button>
        </div>

        <div style={styles.channelList}>
          <ChannelListBody
            loading={loadingChannels}
            error={loadError}
            channels={filteredChannels}
            selectedIds={selectedIds}
            onToggle={toggleChannel}
          />
        </div>
      </div>

      {/* Period selector */}
      <div style={styles.section}>
        <div style={styles.sectionTitle}>{isReport ? 'Report scope' : 'Period'}</div>
        <div style={styles.chipRow}>
          {(['single', 'last3', 'last6', 'last12', 'ytd', 'all', 'custom'] as PeriodPreset[]).map(p => (
            <Pill
              key={p}
              tone="neutral"
              active={periodPreset === p}
              onClick={() => setPeriodPreset(p)}
            >
              {periodLabel(p)}
            </Pill>
          ))}
        </div>
        {periodPreset === 'single' && (
          <div style={styles.customRow}>
            <label style={styles.muted}>
              Month&nbsp;
              <input type="month" value={singleMonth} onChange={(e) => setSingleMonth(e.target.value)} style={styles.monthInput} />
            </label>
          </div>
        )}
        {periodPreset === 'custom' && (
          <div style={styles.customRow}>
            <label style={styles.muted}>
              From&nbsp;
              <input type="month" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} style={styles.monthInput} />
            </label>
            <label style={styles.muted}>
              To&nbsp;
              <input type="month" value={customTo} onChange={(e) => setCustomTo(e.target.value)} style={styles.monthInput} />
            </label>
          </div>
        )}
        <div style={styles.muted}>
          {periodPreset === 'single' ? `Month: ${from || '—'}` : `Range: ${from || '—'} → ${to || '—'}`}
        </div>
      </div>

      {progressLog.length > 0 && (
        <div style={styles.progressBox}>
          {progressLog.map((line, i) => (
            <div key={i} style={styles.progressLine}>{line}</div>
          ))}
        </div>
      )}
      {genError && <div style={styles.error}>{genError}</div>}
      {result?.deckUrl && (
        <div style={styles.resultBox}>
          <div style={{ fontWeight: 600, marginBottom: 8 }}>{isReport ? '✓ Report ready' : '✓ Deck ready'}</div>
          <a href={result.deckUrl} target="_blank" rel="noreferrer" style={styles.linkPrimary}>
            Open in Gamma
          </a>
          {result.exportUrl && (
            <a href={result.exportUrl} target="_blank" rel="noreferrer" style={{ ...styles.linkSecondary, marginLeft: 8 }}>
              {isReport ? 'Download PDF' : 'Download export'}
            </a>
          )}
          {result.truncationApplied && result.truncationApplied !== 'none' && (
            <div style={{ ...styles.muted, marginTop: 8 }}>
              Note: data was trimmed to fit Gamma's prompt budget ({result.truncationApplied}).
            </div>
          )}
        </div>
      )}
    </Modal>
  );
};

interface ChannelListBodyProps {
  loading: boolean;
  error: string | null;
  channels: KnownChannel[];
  selectedIds: Set<string>;
  onToggle: (channelId: string) => void;
}

const ChannelListBody: React.FC<ChannelListBodyProps> = ({ loading, error, channels, selectedIds, onToggle }) => {
  if (loading) return <div style={{ padding: '8px 4px' }}><Spinner label="Loading channels…" /></div>;
  if (error) return <div style={styles.error}>{error}</div>;
  if (channels.length === 0) return <div style={styles.muted}>No channels match that search. Try a different name.</div>;
  return (
    <>
      {channels.map(c => (
        <label key={c.channelId} style={styles.channelRow}>
          <input
            type="checkbox"
            checked={selectedIds.has(c.channelId)}
            onChange={() => onToggle(c.channelId)}
          />
          <span style={{ flex: 1 }}>{c.channelName}</span>
          <span style={styles.badgeRow}>
            {c.hasDirectSold && <span style={{ ...styles.badge, background: 'var(--accent-soft)', color: 'var(--accent-soft-fg)' }}>DS</span>}
            {c.hasAdSense && <span style={{ ...styles.badge, background: 'var(--success-soft)', color: 'var(--success)' }}>AS</span>}
            <span style={styles.muted}>{c.monthCount} mo</span>
          </span>
        </label>
      ))}
    </>
  );
};

function periodLabel(p: PeriodPreset): string {
  switch (p) {
    case 'single': return 'Single month';
    case 'last3': return 'Last 3 months';
    case 'last6': return 'Last 6 months';
    case 'last12': return 'Last 12 months';
    case 'ytd': return 'YTD';
    case 'all': return 'Lifetime';
    case 'custom': return 'Custom';
  }
}

const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

function prettyMonth(monthKey: string): string {
  const m = /^(\d{4})-(\d{2})$/.exec(monthKey || '');
  if (!m) return monthKey || '—';
  const idx = parseInt(m[2], 10) - 1;
  return idx >= 0 && idx <= 11 ? `${MONTH_NAMES[idx]} ${m[1]}` : monthKey;
}

/** Human-readable label for the report cover (only used for the document/report output). */
function computeScopeLabel(preset: PeriodPreset, from: string, to: string): string {
  if (preset === 'all') return 'Lifetime';
  if (preset === 'single' || from === to) return prettyMonth(from);
  return `${prettyMonth(from)} – ${prettyMonth(to)}`;
}

function currentMonthKey(): string {
  const d = new Date();
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
}

function shiftMonth(monthKey: string, deltaMonths: number): string {
  const [y, m] = monthKey.split('-').map(Number);
  const totalMonths = y * 12 + (m - 1) + deltaMonths;
  const ny = Math.floor(totalMonths / 12);
  const nm = (totalMonths % 12) + 1;
  return `${ny}-${String(nm).padStart(2, '0')}`;
}

function computePeriodBounds(
  preset: PeriodPreset,
  customFrom: string,
  customTo: string,
  singleMonth: string,
): { from: string; to: string } {
  const now = currentMonthKey();
  switch (preset) {
    case 'single': return { from: singleMonth, to: singleMonth };
    case 'last3':  return { from: shiftMonth(now, -2), to: now };
    case 'last6':  return { from: shiftMonth(now, -5), to: now };
    case 'last12': return { from: shiftMonth(now, -11), to: now };
    case 'ytd':    return { from: `${new Date().getUTCFullYear()}-01`, to: now };
    // "Lifetime" — the backend returns only what exists in cmFinancialPeriods anyway,
    // so a very early lower bound is safe and saves us a separate "min month" query.
    case 'all':    return { from: '2018-01', to: now };
    case 'custom': return { from: customFrom, to: customTo };
  }
}

const styles: Record<string, React.CSSProperties> = {
  section: { display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 },
  sectionTitle: {
    fontSize: 11, fontWeight: 700, textTransform: 'uppercase',
    letterSpacing: '0.08em', color: 'var(--fg-4)',
  },
  muted: { fontSize: 12, color: 'var(--fg-3)' },
  searchRow: { display: 'flex', gap: 8, alignItems: 'center' },
  searchInput: {
    flex: 1, padding: '8px 12px',
    border: '1px solid var(--border-2)', borderRadius: 'var(--radius-md)', fontSize: 13,
  },
  channelList: {
    maxHeight: 240, overflowY: 'auto',
    border: '1px solid var(--border-2)', borderRadius: 'var(--radius-md)', padding: 4,
  },
  channelRow: {
    display: 'flex', alignItems: 'center', gap: 8,
    padding: '6px 8px', fontSize: 13, cursor: 'pointer', borderRadius: 'var(--radius-sm)',
  },
  badgeRow: { display: 'flex', gap: 6, alignItems: 'center' },
  badge: { fontSize: 10, padding: '2px 6px', borderRadius: 'var(--radius-sm)', fontWeight: 700 },
  chipRow: { display: 'flex', flexWrap: 'wrap', gap: 8 },
  customRow: { display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' },
  monthInput: {
    padding: '6px 8px',
    border: '1px solid var(--border-2)', borderRadius: 'var(--radius-md)', fontSize: 13,
  },
  progressBox: {
    background: 'var(--cg-neutral-25)',
    border: '1px solid var(--border-2)',
    borderRadius: 'var(--radius-md)', padding: 12, maxHeight: 160, overflowY: 'auto',
    fontFamily: 'var(--font-mono, monospace)', fontSize: 12,
  },
  progressLine: { color: 'var(--fg-1)', lineHeight: 1.5 },
  error: {
    background: 'var(--danger-soft)', border: '1px solid var(--cg-terra-200)', color: 'var(--danger)',
    padding: '10px 12px', borderRadius: 'var(--radius-md)', fontSize: 13, marginTop: 12,
  },
  resultBox: {
    background: 'var(--success-soft)', border: '1px solid var(--cg-forest-200)', color: 'var(--success)',
    padding: 12, borderRadius: 'var(--radius-md)', fontSize: 13, marginTop: 12,
  },
  linkPrimary: {
    padding: '8px 16px', borderRadius: 'var(--radius-md)', background: 'var(--accent)', color: '#fff',
    fontSize: 13, fontWeight: 600, textDecoration: 'none', display: 'inline-block',
  },
  linkSecondary: {
    padding: '6px 12px', borderRadius: 'var(--radius-md)',
    border: '1px solid var(--border-2)',
    background: 'var(--cg-neutral-25)', color: 'var(--fg-1)',
    fontSize: 12, fontWeight: 500, textDecoration: 'none', display: 'inline-block',
  },
};

export default IncomeDeckModal;
